import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Crown, Sparkles, FileText, GitCompare, MessageSquare, Loader2 } from "lucide-react";

interface UpgradePromptProps {
  onUpgrade: (plan: 'starter' | 'pro') => void;
  isUpgrading: boolean;
}

export function UpgradePrompt({ onUpgrade, isUpgrading }: UpgradePromptProps) {
  return (
    <Card className="max-w-3xl mx-auto border-2 border-amber-200 bg-gradient-to-br from-amber-50 to-orange-50 shadow-xl rounded-3xl">
      <CardHeader className="text-center pb-2">
        <div className="mx-auto w-16 h-16 bg-amber-100 rounded-full flex items-center justify-center mb-4">
          <Sparkles className="h-8 w-8 text-amber-600" />
        </div>
        <CardTitle className="text-2xl font-bold text-slate-800">
          Kostenlose Analysen aufgebraucht
        </CardTitle>
        <p className="text-slate-600 mt-2">
          Sie haben alle 3 kostenlosen Analysen für heute verwendet. 
          Upgraden Sie jetzt für mehr Analysen und erweiterte Funktionen!
        </p>
      </CardHeader>
      <CardContent className="pt-4">
        <div className="grid md:grid-cols-2 gap-4">
          {/* Starter Plan */}
          <div className="bg-white rounded-2xl p-6 border border-slate-200 shadow-md hover:shadow-lg transition-all">
            <div className="flex items-center gap-2 mb-3">
              <FileText className="h-5 w-5 text-primary" />
              <h3 className="text-lg font-bold text-slate-800">Starter</h3>
            </div>
            <p className="text-3xl font-bold text-slate-800 mb-1">
              9,90 € <span className="text-sm font-normal text-slate-500">/Monat</span>
            </p>
            <ul className="space-y-2 my-4 text-sm text-slate-600">
              <li className="flex items-center gap-2">
                <span className="text-primary">✓</span> 25 Analysen pro Tag
              </li>
              <li className="flex items-center gap-2">
                <span className="text-primary">✓</span> Analyse-Verlauf
              </li>
              <li className="flex items-center gap-2">
                <span className="text-primary">✓</span> PDF-Export
              </li>
            </ul>
            <Button 
              className="w-full rounded-xl bg-gradient-to-r from-primary to-blue-600 hover:shadow-lg"
              onClick={() => onUpgrade('starter')}
              disabled={isUpgrading}
            >
              {isUpgrading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
              Starter wählen
            </Button>
          </div>

          {/* Pro Plan */}
          <div className="bg-gradient-to-br from-amber-50 to-yellow-50 rounded-2xl p-6 border-2 border-amber-300 shadow-md hover:shadow-lg transition-all relative">
            <div className="absolute -top-3 right-4 bg-amber-500 text-white text-xs font-bold px-3 py-1 rounded-full">
              BELIEBT
            </div>
            <div className="flex items-center gap-2 mb-3">
              <Crown className="h-5 w-5 text-amber-600" />
              <h3 className="text-lg font-bold text-slate-800">Pro</h3>
            </div>
            <p className="text-3xl font-bold text-slate-800 mb-1">
              19,90 € <span className="text-sm font-normal text-slate-500">/Monat</span>
            </p>
            <ul className="space-y-2 my-4 text-sm text-slate-600">
              <li className="flex items-center gap-2">
                <span className="text-amber-600">✓</span> 100 Analysen pro Tag
              </li>
              <li className="flex items-center gap-2">
                <span className="text-amber-600">✓</span> Alle Starter-Funktionen
              </li>
              <li className="flex items-center gap-2">
                <GitCompare className="h-4 w-4 text-amber-600" /> Dokument-Vergleich
              </li>
              <li className="flex items-center gap-2">
                <MessageSquare className="h-4 w-4 text-amber-600" /> Dokument Q&A
              </li>
              <li className="flex items-center gap-2">
                <span className="text-amber-600">✓</span> Batch-Upload
              </li>
            </ul>
            <Button 
              className="w-full rounded-xl bg-gradient-to-r from-amber-500 to-yellow-500 hover:shadow-lg text-amber-950"
              onClick={() => onUpgrade('pro')}
              disabled={isUpgrading}
            >
              {isUpgrading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Crown className="h-4 w-4 mr-2" />}
              Pro wählen
            </Button>
          </div>
        </div>

        <p className="text-center text-xs text-slate-500 mt-6">
          Sichere Zahlung über Stripe. Jederzeit kündbar.
        </p>
      </CardContent>
    </Card>
  );
}
